import React, { useCallback, useRef, useState } from 'react';
import type { BoardCommentThread } from '../../../types';
import { useBoardComments } from './BoardCommentsContext';
import RichCommentEditor, {
  type RichCommentEditorHandle,
  type RichCommentEditorPayload,
} from '../../pm/RichCommentEditor';
import RichCommentRenderer from '../../pm/RichCommentRenderer';
import '../../pm/TaskComments.css';
import './BoardComments.css';
import CommentScheduleControls from '../../pm/CommentScheduleControls';
import { createScheduledBoardComment } from '../../../utils/scheduledComments';
import { db } from '../../../utils/firebase';

function formatTime(d: Date): string {
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

interface BoardCommentThreadProps {
  nodeId: string;
  thread?: BoardCommentThread;
}

const BoardCommentThreadPanel: React.FC<BoardCommentThreadProps> = ({ nodeId, thread }) => {
  const { author, dealId, postComment, posting, mentionableUsers, highlightedNodeId } = useBoardComments();
  const editorRef = useRef<RichCommentEditorHandle>(null);
  const [hasDraft, setHasDraft] = useState(false);

  const handleSubmit = useCallback(async (payload?: RichCommentEditorPayload) => {
    const next = payload || editorRef.current?.getPayload();
    if (!next || next.isEmpty || posting) return;
    await postComment(nodeId, next.bodyPlain, next.mentions, next.bodyHtml);
    editorRef.current?.clear();
    setHasDraft(false);
  }, [nodeId, postComment, posting]);

  return (
    <div
      className={`board-comment-bubble nodrag nowheel${highlightedNodeId === nodeId ? ' is-highlight' : ''}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="board-comment-bubble-scroll">
        {!thread ? (
          <p className="board-comment-empty">No comments yet.</p>
        ) : (
          <>
            <div className="board-comment-msg">
              <div className="board-comment-msg-head">
                <strong>{thread.authorName}</strong>
                <span>{formatTime(thread.createdAt)}</span>
              </div>
              <RichCommentRenderer body={thread.content} className="board-comment-rich" />
            </div>
            {thread.replies.map((reply, idx) => (
              <div key={idx} className="board-comment-msg board-comment-msg--reply">
                <div className="board-comment-msg-head">
                  <strong>{reply.userName}</strong>
                  <span>{formatTime(reply.createdAt)}</span>
                </div>
                <RichCommentRenderer body={reply.content} className="board-comment-rich" />
              </div>
            ))}
          </>
        )}
      </div>
      {author.canWrite && (
        <div className="board-comment-compose">
          <RichCommentEditor
            ref={editorRef}
            mentionableUsers={mentionableUsers}
            taskId={nodeId}
            enablePasteImage={false}
            hideToolbar
            disabled={posting}
            placeholder="Write a comment… (@ to mention)"
            onChange={(html) => setHasDraft(!!html && html !== '<p></p>')}
            onSubmit={(payload) => void handleSubmit(payload)}
          />
          <div className="board-comment-send-row">
            <CommentScheduleControls
              disabled={!hasDraft || posting}
              onSchedule={async (when) => {
                const next = editorRef.current?.getPayload();
                if (!next || next.isEmpty) return;
                await createScheduledBoardComment({
                  db,
                  authorId: author.uid,
                  authorName: author.displayName,
                  scheduledAt: when,
                  dealId,
                  nodeId,
                  content: next.bodyPlain,
                  contentHtml: next.bodyHtml,
                  mentions: next.mentions,
                });
                editorRef.current?.clear();
                setHasDraft(false);
              }}
            />
            <button
              type="button"
              className="board-comment-send"
              disabled={!hasDraft || posting}
              onClick={() => void handleSubmit()}
            >
              {posting ? '…' : thread ? 'Reply' : 'Comment'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BoardCommentThreadPanel;
